import type { ProductVariant } from '../types/pz';
import { normalizeUnitCode } from './units';
import { getProductSelectionLabel } from './productSelectorLabel';
import type { QuantityUnit } from './commercialQuantity';

/**
 * Normalize any incoming unit to a canonical WZ line code.
 *
 * Delegates to normalizeUnitCode in utils/units.ts, which is THE frontend home
 * for this mapping and an exact mirror of backend unitLabels.js. It used to be a
 * second, independent table here and had drifted on LINEAR_METER ('m' here vs
 * 'mb' on the backend), so the on-screen total and the PDF total could print
 * different units for the same quantity. `LINEAR_METER` is not in the deployed
 * `ProductUnit` enum today, so that specific drift was latent — but the two
 * tables feed the same totals and must not be allowed to diverge again.
 *
 * A catalog-linked WZ line keeps the transaction unit chosen by the operator;
 * free-text lines behave the same. Both paths still need normalization:
 * order lines and older WZ rows carry raw `ProductUnit` enums ('PIECE'), which
 * must never reach a Polish screen as-is.
 *   - already a line code ('pcs', 'm2', …) → returned unchanged
 *   - ProductUnit enum ('PIECE', 'SQUARE_METER', …) → mapped
 *   - empty / unknown → 'pcs'
 */
export function toWzUnitCode(unit?: string | null): string {
  return normalizeUnitCode(unit) ?? 'pcs';
}

/** Existing WZ-line fields the autofill reads for its fallback chains. */
export interface WzVariantAutofillLine {
  quantity?: number;
  unit?: string;
  productName?: string;
  productCode?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}

/** Fields the autofill writes onto a WZ line. Spread onto the existing item. */
export interface WzVariantAutofillPatch {
  productVariantId?: string;
  productName?: string;
  productCode?: string;
  unit?: string;
  saleQuantity?: number | null;
  saleUnit?: QuantityUnit | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}

/**
 * Single home for "apply a catalog variant selection onto a WZ line". Mirrors
 * getVariantOrderLinePatch (modules/products/orderVariantSelection.ts) /
 * buildSupplierOrderVariantAutofill (modules/orders/supplierOrderVariantUtils.ts)
 * for the WZ document surface. Used by both the WZ
 * create form (WZNew) and the WZ edit form (WZDetail) so the autofill rule cannot drift.
 *
 * Returns a patch to spread onto the current line. Clearing the picker (no variantId)
 * only unsets `productVariantId`, leaving the operator's other fields intact so the
 * free-text productName entry still works.
 */
export function buildWzVariantAutofill(
  line: WzVariantAutofillLine,
  variantId: string | undefined,
  variant?: ProductVariant
): WzVariantAutofillPatch {
  if (!variantId || !variant) {
    return { productVariantId: undefined };
  }
  const unit = toWzUnitCode(line.unit);
  const netWeight = variant.netWeight ?? variant.product?.netWeight;
  const grossWeight = variant.grossWeight ?? variant.product?.grossWeight;
  return {
    productVariantId: variantId,
    productName: getProductSelectionLabel(variant, line.productName || ''),
    productCode: variant.product?.code || line.productCode || '',
    unit,
    saleQuantity: Number(line.quantity) || 1,
    saleUnit: unit as QuantityUnit,
    width: variant.product?.width ?? variant.width ?? line.width,
    height: variant.product?.height ?? variant.height ?? line.height,
    length: variant.product?.length ?? variant.length ?? line.length,
    thickness: variant.product?.thickness ?? variant.thickness ?? line.thickness,
    weight: grossWeight ?? netWeight ?? line.weight,
    netWeight: netWeight ?? line.netWeight,
    grossWeight: grossWeight ?? line.grossWeight,
    productCategoryDimensionDisplayProfile:
      variant.product?.productCategoryDimensionDisplayProfile
      ?? line.productCategoryDimensionDisplayProfile
      ?? null,
  };
}
